import { useMutation } from '@apollo/client';
import { BuyerUserRole } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import type {
  UsersPageBuyerAddUserMutation,
  UsersPageBuyerAddUserMutationVariables,
  UsersPageBuyerRemoveUserMutation,
  UsersPageBuyerRemoveUserMutationVariables,
} from '../../../generated';
import { BuyerSelectOption } from '../components/BuyerLinking/types';
import { BUYER_ADD_USER_MUTATION } from '../data/mutations/buyerAddUser.gql';
import { BUYER_REMOVE_USER_MUTATION } from '../data/mutations/buyerRemoveUser.gql';

export interface UseBuyerLinkingResponse {
  linkUserToBuyer: (buyer: BuyerSelectOption, userId: string) => Promise<void>;
  unlinkUserFromBuyer: (buyerId: string, userId: string) => Promise<void>;
  isLinking: boolean;
  isUnlinking: boolean;
}

export const useBuyerLinking = (): UseBuyerLinkingResponse => {
  const { addNotification } = useNotification();

  const [buyerAddUser, { loading: isLinking }] = useMutation<
    UsersPageBuyerAddUserMutation,
    UsersPageBuyerAddUserMutationVariables
  >(BUYER_ADD_USER_MUTATION);

  const [buyerRemoveUser, { loading: isUnlinking }] = useMutation<
    UsersPageBuyerRemoveUserMutation,
    UsersPageBuyerRemoveUserMutationVariables
  >(BUYER_REMOVE_USER_MUTATION);

  const linkUserToBuyer = useCallback(
    async (buyer: BuyerSelectOption, userId: string) => {
      try {
        await buyerAddUser({
          variables: {
            buyerId: buyer.value,
            userId,
            input: buyer.metaData?.rolesEnabled
              ? {
                  role: buyer.metaData.buyerUserRole as BuyerUserRole,
                }
              : {
                  role: BuyerUserRole.STAFF,
                },
          },
        });

        addNotification({
          type: 'success',
          title: 'User Linked',
          message: `Successfully linked user to ${buyer.label}`,
        });
      } catch (error) {
        // Error linking user to buyer
        addNotification({
          type: 'error',
          title: 'Linking Failed',
          message: 'Failed to link user to buyer. Please try again.',
        });
        throw error;
      }
    },
    [buyerAddUser, addNotification]
  );

  const unlinkUserFromBuyer = useCallback(
    async (buyerId: string, userId: string) => {
      try {
        await buyerRemoveUser({
          variables: {
            buyerId,
            userId,
          },
        });

        addNotification({
          type: 'success',
          title: 'User Unlinked',
          message: 'Successfully removed user from buyer',
        });
      } catch (error) {
        // Error unlinking user from buyer
        addNotification({
          type: 'error',
          title: 'Unlinking Failed',
          message: 'Failed to remove user from buyer. Please try again.',
        });
        throw error;
      }
    },
    [buyerRemoveUser, addNotification]
  );

  return {
    linkUserToBuyer,
    unlinkUserFromBuyer,
    isLinking,
    isUnlinking,
  };
};
